import { useState } from 'react';
import { Tooltip } from '@spotted-zebra-uk/ui-components';
import {
  ProjectCandidateStageCategory,
  useProjectGetCandidateStageBreakdownQuery,
  useProjectGetInterviewLinkCountQuery,
} from 'generated/graphql';
import ProjectCandidatesTooltip from './ProjectCandidatesTooltip';
import { getCandidatesAriaLabel } from './ProjectCandidatesCell.helpers';
import styles from './ProjectCandidatesCell.module.scss';

export const PROJECT_CANDIDATES_CELL_DATA_TEST_ID = 'project-candidates-cell';

const ProjectCandidatesCell = ({
  projectId,
  candidatesCount,
  roleStageCategories,
}: {
  projectId: number;
  candidatesCount: number;
  roleStageCategories: ProjectCandidateStageCategory[];
}) => {
  // The breakdown is a query of its own so the table does not pay for it. It is
  // fired the first time the tooltip is opened and then served from the Apollo
  // cache for the rest of the page session.
  const [isBreakdownRequested, setIsBreakdownRequested] = useState(false);

  const { data, loading, error } = useProjectGetCandidateStageBreakdownQuery({
    variables: { projectId },
    skip: !isBreakdownRequested,
  });

  // Its own query because it reaches the meeting service: when that is down the
  // breakdown still renders and only the interview line says so.
  const {
    data: interviewLinkData,
    loading: isInterviewLinkLoading,
    error: interviewLinkError,
  } = useProjectGetInterviewLinkCountQuery({
    variables: { projectId },
    skip: !isBreakdownRequested,
  });

  const breakdown = data?.ProjectGetCandidateStageBreakdown;

  if (!candidatesCount) {
    return (
      <span data-testid={`${PROJECT_CANDIDATES_CELL_DATA_TEST_ID}-empty`}>
        {candidatesCount}
      </span>
    );
  }

  const requestBreakdown = () => setIsBreakdownRequested(true);

  return (
    // Tooltip overwrites its child's data-testid with its own, so the stable
    // handle for this cell lives on the wrapper.
    <span data-testid={PROJECT_CANDIDATES_CELL_DATA_TEST_ID}>
      <Tooltip
        ariaLabel={getCandidatesAriaLabel(
          candidatesCount,
          roleStageCategories,
          breakdown
        )}
        globalCloseEvents={{ escape: true }}
        render={() => (
          <ProjectCandidatesTooltip
            breakdown={breakdown}
            candidatesCount={candidatesCount}
            hasError={Boolean(error)}
            hasInterviewLinkError={Boolean(interviewLinkError)}
            interviewLinkCount={interviewLinkData?.ProjectGetInterviewLinkCount}
            isInterviewLinkLoading={isInterviewLinkLoading}
            isLoading={!error && (loading || !breakdown)}
            roleStageCategories={roleStageCategories}
          />
        )}
      >
        <span
          className={styles.trigger}
          onFocus={requestBreakdown}
          onMouseEnter={requestBreakdown}
          tabIndex={0}
        >
          {candidatesCount}
        </span>
      </Tooltip>
    </span>
  );
};

export default ProjectCandidatesCell;
